import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { Form, ErrorMessage, Field, Formik } from "formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { addCountrySchema } from "components/Common/validation"
import {
  addCountry,
  getCountryDetails,
  updateCountry,
} from "store/actions/countriesAction"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  name: "",
  nameAr: "",
  dialCode: "",
  countryCode: "",
  displayOrder: "",
  title: "",
  titleAr: "",
  value: "",
  registeredNo: "",
  currency: "",
  status: 1,
  currencySymbol: "",
}

const AddCountry = ({ mode }) => {
  const { t } = useTranslation()

  document.title = `${
    mode === "add" ? t("Add") : t("Edit")
  } ${"Country | Namq"}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, isLoading, countryDetails } = useSelector(
    state => state.Countries
  )
  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      11,
      "country_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      11,
      "country_3",
      accountType
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    if (mode === "edit") {
      fetchCountryDetails()
    }
  }, [])

  const fetchCountryDetails = () => {
    const payload = { id }
    dispatch(getCountryDetails(payload))
  }

  useEffect(() => {
    if (countryDetails && !loading && mode === "edit") {
      const {
        taxInfo,
        name,
        nameAr,
        countryCode,
        dialCode,
        status,
        displayOrder,
        currency,
        currencySymbol,
      } = countryDetails
      //   console.log(taxInfo)
      setFormValues({
        ...formValues,
        name,
        nameAr,
        dialCode,
        countryCode,
        currencySymbol,
        displayOrder,
        title: taxInfo?.title,
        titleAr: taxInfo?.titleAr,
        value: taxInfo?.value,
        registeredNo: taxInfo?.registeredNo,
        currency,
        status,
      })
    }
  }, [countryDetails])

  const handleOnSubmit = values => {
    const {
      name,
      nameAr,
      dialCode,
      countryCode,
      displayOrder,
      title,
      titleAr,
      value,
      registeredNo,
      currency,
      status,
      currencySymbol,
    } = values

    const payload = {
      name,
      nameAr,
      dialCode,
      countryCode,
      displayOrder,
      currency,
      currencySymbol,
      taxInfo: {
        title,
        titleAr,
        value,
        registeredNo,
      },
    }
    if (mode === "add") {
      dispatch(addCountry(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/countries`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const editPayload = {
        ...payload,
        id,
        status: Number(status),
      }
      dispatch(updateCountry(editPayload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("Country")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addCountrySchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(English)")}
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              placeholder={t("Name(English)")}
                              className={`form-control ${
                                touched.name && errors.name ? "is-invalid" : ""
                              }`}
                            />
                            <TextError name={"name"} />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>
                              {t("Name(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="nameAr"
                              name="nameAr"
                              placeholder={t("Name(عربي)")}
                              className={`form-control ${
                                touched.nameAr && errors.nameAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name={"nameAr"} />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="countryCode">
                              <span className="text-danger">*</span>{" "}
                              {t("Country Code")}
                            </label>
                            <Field
                              type="text"
                              id="countryCode"
                              name="countryCode"
                              placeholder={t("Country Code")}
                              className={`form-control ${
                                touched.countryCode && errors.countryCode
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name={"countryCode"} />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="dialCode">
                              <span className="text-danger">*</span>{" "}
                              {t("Dial Code")}
                            </label>
                            <Field
                              type="text"
                              id="dialCode"
                              name="dialCode"
                              placeholder={t("Dial Code")}
                              className={`form-control ${
                                touched.dialCode && errors.dialCode
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name={"dialCode"} />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="displayOrder">
                              <span className="text-danger">*</span>{" "}
                              {t("Display Order")}
                            </label>
                            <Field
                              type="text"
                              id="displayOrder"
                              name="displayOrder"
                              placeholder={t("Display Order")}
                              className={`form-control ${
                                touched.displayOrder && errors.displayOrder
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name={"displayOrder"} />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="currency">
                              <span className="text-danger">*</span>{" "}
                              {t("Currency Code")}
                            </label>
                            <Field
                              type="text"
                              id="currency"
                              name="currency"
                              placeholder={t("Currency Code")}
                              className={`form-control ${
                                touched.currency && errors.currency
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="currency" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="currencySymbol">
                              <span className="text-danger">*</span>{" "}
                              {t("Currency Symbol")}
                            </label>
                            <Field
                              type="text"
                              id="currencySymbol"
                              name="currencySymbol"
                              placeholder={t("Currency Symbol")}
                              className={`form-control ${
                                touched.currencySymbol && errors.currencySymbol
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="currencySymbol" />
                          </div>
                        </div>
                        <CardTitle className="mt-2">
                          {t("Tax Details")}
                        </CardTitle>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="title">
                              <span className="text-danger">*</span>
                              {t("Name(English)")}
                            </label>
                            <Field
                              type="text"
                              id="title"
                              name="title"
                              placeholder={t("Name(English)")}
                              className={`form-control ${
                                touched.title && errors.title
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="title" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="titleAr">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="titleAr"
                              name="titleAr"
                              placeholder={t("Name(عربي)")}
                              className={`form-control ${
                                touched.titleAr && errors.titleAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="titleAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="value">
                              <span className="text-danger">*</span>{" "}
                              {t("Tax Rate (in %)")}
                            </label>
                            <Field
                              type="text"
                              id="value"
                              name="value"
                              placeholder={t("Tax Rate (in %)")}
                              className={`form-control ${
                                touched.value && errors.value
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="value" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="registeredNo">
                              <span className="text-danger">*</span>{" "}
                              {t("Registered No")}
                            </label>
                            <Field
                              type="text"
                              id="registeredNo"
                              name="registeredNo"
                              placeholder={t("Registered No")}
                              className={`form-control ${
                                touched.registeredNo && errors.registeredNo
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="registeredNo" />
                          </div>
                        </div>

                        <div className="col-12 d-flex justify-content-end mt-5 mb-5">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddCountry
